import { useEffect, useMemo, useState } from 'react';
import { ValuesType } from 'utility-types';

import { Menu } from '~/modules/elements/akui/menu';
import { NavButton } from '~/modules/elements/nav-controls';
import GameState from '~/modules/game-engine/game-state/game-state';
import events from '~/modules/game-events/game-events';
import { useEventListenerSelector } from '~/modules/game-events/hooks';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useMicMonitoring from '~/modules/hooks/use-mic-monitoring';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import { assignProfileToPlayer, profileOnSlot } from '~/modules/user-profiles/assign';
import { useProfiles } from '~/modules/user-profiles/hooks';
import isChromium from '~/modules/utils/is-chromium';
import isWindows from '~/modules/utils/is-windows';
import { MIC_SETUP_TITLE } from '~/routes/keyboard-help/screen-titles';
import ProfileFormModal from '~/routes/profiles/profile-form-modal';
import commitMicSetup from '~/routes/select-input/commit-mic-setup';
import { useMicrophoneList } from '~/routes/select-input/hooks/use-microphone-list';
import { useRemoteMicAutoselect } from '~/routes/select-input/hooks/use-remote-mic-autoselect';
import { useSingstarAutoselect } from '~/routes/select-input/hooks/use-singstar-autoselect';
import { DummyInputSource } from '~/routes/select-input/input-sources/dummy';
import { InputSourceList, InputSourceNames } from '~/routes/select-input/input-sources/interfaces';
import { MicrophoneInputSource } from '~/routes/select-input/input-sources/microphone';
import JoinPhonePanel from '~/routes/select-input/join-phone-panel';
import MicSlot from '~/routes/select-input/mic-slot';
import { profileHolders } from '~/routes/select-input/singer-options';
import useMicSlots from '~/routes/select-input/use-mic-slots';
import { MicSetupPreference } from '~/routes/settings/settings-state';

interface Props {
  /** Called once the setup is committed, with what ended up behind the slots. */
  onFinish?: (pref: ValuesType<typeof MicSetupPreference>) => void;
  /**
   * Whether Backspace can leave this screen. First-run setup has nothing behind it, so it passes
   * nothing and the key stays inert; every other caller leaves the same way `← Done` does.
   */
  canGoBack?: boolean;
}

/** 1600 of the frame's 1920, matching the other wide screens rather than the 45rem menu column. */
export const MIC_SETUP_CARD = 'sm:max-w-[min(94vw,100rem)] lg:max-w-[min(94vw,100rem)] 2xl:max-w-[min(94vw,100rem)]';

/**
 * Every slot's source except `Dummy`, which is the absence of one: "Change" walks the inputs a
 * player could actually sing into, and emptying a slot is what the ✕ is for.
 */
const withoutDummy = (inputs: Partial<Record<InputSourceNames, InputSourceList>>) => {
  const rest = { ...inputs };
  delete rest[DummyInputSource.inputName];
  return rest as Record<string, InputSourceList>;
};

/**
 * One slot per player, in the player's colour, with a live level bar — so what is set here is what
 * is seen while singing. There is no "how do you want to sing?" step: a phone joins through the band
 * at the foot of the screen and takes the next free slot, a local mic is one button, and every slot
 * can be pointed somewhere else individually.
 */
function SelectInputView({ onFinish, canGoBack = false }: Props) {
  useMicMonitoring();
  useRemoteMicAutoselect();
  useSingstarAutoselect();

  // Phones in the room without a slot are offered a colour as the screen opens - but not from the
  // pause menu, where a new player cannot be added and taking a held colour would swap a singer.
  useEffect(() => {
    if (!GameState.isPlaying()) RemoteMicServer.callAllClients('notifyPlayerSettingsOpen');
  }, []);

  const inputs = useMicrophoneList(true);
  // With no real source at all (permission refused, no devices) the slots fall back to the full list
  // so `usePlayerInput` still has a source to name - it reads `sources` unguarded.
  const realSources = useMemo(() => withoutDummy(inputs), [inputs]);
  const sources = useMemo(
    () => (Object.keys(realSources).length > 0 ? realSources : (inputs as Record<string, InputSourceList>)),
    [inputs, realSources],
  );
  const slots = useMicSlots();
  // Past four the cards go two rows of four, which only fits a 1080 frame with smaller cards.
  const dense = slots.length > 4;
  const profiles = useProfiles();
  // Which slot asked for a new singer, so the profile the form creates lands on the card it was
  // opened from. One form for the screen rather than one per card: a modal per card is a backdrop
  // per card, and only one of them can ever be open.
  const [newSingerFor, setNewSingerFor] = useState<PlayerNumber | null>(null);
  // Read once on mount rather than watched: this screen is also reached from the pause menu, and a
  // song that was playing when it opened is still playing when it closes.
  const rosterLocked = GameState.isPlaying();
  const ready = slots.filter((slot) => slot.hasInput).length;
  const slotSingerNames = useEventListenerSelector(
    [events.playerNameChanged, events.playerInputChanged, events.playerAdded, events.playerRemoved],
    () => slots.map((slot) => PlayersManager.getPlayer(slot.number)?.getName()),
    [slots],
  );
  const heldBy = profileHolders(
    slotSingerNames.map((name) => profileOnSlot(profiles, name)?.id),
    slots.filter((slot) => slot.hasInput).map((slot) => slot.number),
  );

  // Two SingStar channels on one device is the case Chrome mishandles; a single mic is fine.
  const singstarOnChrome = useEventListenerSelector([events.playerInputChanged, events.inputListChanged], () => {
    const micInputs = PlayersManager.getInputs().filter((input) => input.source === 'Microphone');
    return micInputs.length > 1 && isChromium() && isWindows();
  });

  // Every exit commits - see `commitMicSetup`. There is no separate confirm button any more, so
  // there is no exit left that means "I did not mean it".
  const commit = () => onFinish?.(commitMicSetup());

  const addInput = (number: PlayerNumber) => {
    const player = PlayersManager.getPlayer(number) ?? PlayersManager.addPlayer(number);
    if (!player) return;

    // A free DEVICE, not a free channel: two channels of one microphone are one microphone, and
    // handing the second one out as if it were a second mic gives a singer an input nobody is
    // singing into. A SingStar pair is the exception and `use-singstar-autoselect` places it itself.
    //
    // Slots still on `Dummy` hold no device to be taken. Counting them took the machine's own
    // microphone out of the running whenever a slot was unclaimed - every player is created on
    // `Dummy` with `deviceId: 'default'` - so the first singer in the room was handed whatever
    // second input the browser happened to list, an aggregate or virtual device as often as a real
    // one, while `default` sat there unused.
    const takenDevices = new Set(
      PlayersManager.getPlayers()
        .filter((other) => other.number !== number && other.input.source !== DummyInputSource.inputName)
        .map((other) => other.input.deviceId),
    );

    // Walked over `realSources`, not `sources`: `sources` deliberately keeps `Dummy` when nothing
    // real has loaded so the slot cards still have a source to name, and `Dummy` IS the absence of
    // an input - assigning it is what made this button appear to do nothing at all.
    for (const [source, list] of Object.entries(realSources)) {
      const free = list.list.find((input) => !takenDevices.has(input.deviceId));
      if (free) {
        player.changeInput(source as InputSourceNames, free.channel, free.deviceId);
        return;
      }
    }

    // Nothing spare, so share what the room already has - one microphone between two singers still
    // scores them both, where a silent slot scores neither.
    const [firstSource, firstList] = Object.entries(realSources)[0] ?? [];
    const fallback = firstList?.getDefault();
    if (firstSource && fallback) {
      player.changeInput(firstSource as InputSourceNames, fallback.channel, fallback.deviceId);
      return;
    }

    // No real source in the list yet. `useMicrophoneList` drops any source with an empty list, and
    // the microphone list is empty until the permission is granted and every device has been opened
    // once to count its channels - seconds, on a machine with a few inputs. Take the default device
    // by name and let the list catch up and name it, exactly as "Use this device's mic" does.
    player.changeInput(MicrophoneInputSource.inputName, 0, 'default');
  };

  /** The answer to "I have no phone": this machine's own microphone, into the first free slot. */
  const useThisDeviceMic = () => {
    const target = slots.find((slot) => !slot.hasInput)?.number ?? 0;
    const player = PlayersManager.getPlayer(target) ?? PlayersManager.addPlayer(target);
    if (!player) return;

    // Pressed before the device list has loaded (it needs the microphone permission first), the slot
    // still takes the default device by name - the list catches up and names it a moment later.
    const mic = inputs.Microphone?.getDefault();
    player.changeInput(MicrophoneInputSource.inputName, mic?.channel ?? 0, mic?.deviceId ?? 'default');
  };

  const removeInput = (number: PlayerNumber) => {
    const player = PlayersManager.getPlayer(number);
    if (!player) return;

    // The two base slots always exist (`removePlayer` would only re-add them to reach the minimum),
    // so emptying one means pointing it back at `Dummy` - the source every player starts on.
    if (number >= PlayersManager.getMinPlayerNumber()) {
      PlayersManager.removePlayer(number);
    } else {
      player.changeInput(DummyInputSource.inputName, number, 'default');
    }
  };

  const { register, focused } = useKeyboardNav({
    onBackspace: canGoBack ? commit : undefined,
    title: MIC_SETUP_TITLE,
    subtitle: rosterLocked ? 'Paused · changes apply now' : `${ready} of ${slots.length} players ready`,
    // Rows of up to four cards with their own buttons, plus a header row and the join band: which
    // control is "the next one down" is a question about the screen, not about registration order.
    direction: 'grid',
    // The corner help box is fixed top-right at z-1000, which is where the ready count sits.
    presentation: 'strip',
    // Named, or the phone promotes the first non-back button and paints `Clear Player 1` as the
    // thing to press - the brightest object on the screen being the one destructive action on it.
    primary: 'back-button',
    // The profile form has a text field in it, and every keystroke of a name would otherwise be a
    // navigation key on the screen underneath.
    enabled: newSingerFor === null,
  });

  return (
    <KeyboardNavContext value={register}>
      <div className="flex flex-wrap items-center gap-[1.125rem]">
        {/* The only exit, and it commits - which is why it reads Done rather than Back. Always
            rendered, first-run setup included: that screen has nothing behind it, so this is the
            way on rather than the way back. */}
        <NavButton
          name="back-button"
          variant="back"
          size="small"
          className="h-[3.375rem] w-auto px-5"
          remoteLabel="Done"
          // No arrow on the phone in either direction: a leading one promises a way back and a
          // trailing one promises a next step, and this button applies and closes.
          remoteIcon={null}
          isDefault
          onClick={commit}>
          ← Done
        </NavButton>
        <span className="stroke-text typography text-active text-[2.125rem] font-bold">Setup microphones</span>
        <span className="text-xl text-white/50" data-test="slots-ready">
          {ready} of {slots.length} ready
        </span>
      </div>

      <div className={`grid sm:grid-cols-2 xl:grid-cols-4 ${dense ? 'gap-3' : 'gap-5'}`}>
        {slots.map((slot) => (
          <MicSlot
            key={slot.number}
            slot={slot}
            slots={slots}
            sources={sources}
            profiles={profiles}
            heldBy={heldBy}
            focused={focused?.startsWith(`player-${slot.number}-`) ?? false}
            rosterLocked={rosterLocked}
            compact={dense}
            onAdd={() => addInput(slot.number)}
            onRemove={() => removeInput(slot.number)}
            onNewSinger={() => setNewSingerFor(slot.number)}
          />
        ))}
      </div>

      {/* `nested`, because this screen is itself a modal when it is reached from the song settings
          card - and at the base layer the card underneath would render on top of this backdrop. */}
      <ProfileFormModal
        open={newSingerFor !== null}
        profile={null}
        level="nested"
        // Straight onto the slot that asked for it: going to the trouble of naming a singer here and
        // then having to pick them out of the list is the same two steps this replaced.
        onCreated={(created) => newSingerFor !== null && assignProfileToPlayer(newSingerFor, created)}
        onClose={() => setNewSingerFor(null)}
      />

      {singstarOnChrome && (
        <Menu.HelpText data-test="singstar-chrome-tip">
          <strong>Chrome</strong> is known for not handling SingStar mics well. If you notice any problems, try using an
          alternative browser (eg. <strong>MS Edge</strong> or <strong>Firefox</strong>).
        </Menu.HelpText>
      )}

      <JoinPhonePanel compact={dense}>
        <NavButton
          name="use-built-in"
          size="small"
          className="h-16 w-auto px-7 whitespace-nowrap"
          remoteLabel="Use the TV's own mic"
          onClick={useThisDeviceMic}>
          Use this device&#39;s mic
        </NavButton>
      </JoinPhonePanel>
    </KeyboardNavContext>
  );
}

export default SelectInputView;
